import loadable from '@loadable/component';
import React from 'react';
import {
    GA_EXTERNAL_VIEW_EVENT_ACTION,
    GA_VIEW_EVENT_ACTION,
} from '../../../../constants/analytics';
import { SearchEventCardStyleSwitcher } from '../../../../containers/SearchEventCardStyleSwitcher';
import { SearchEventCardStylesSwitcherProps } from '../../../../containers/SearchEventCardStyleSwitcher/SearchEventCardStyleSwitcher';
import { PromotedActionsMenuEventCard } from './PromotedActionsMenuEventCard';

const ExpandDedupedEventCTA = loadable(() => import('./ExpandDedupedEventCTA'));
const SearchEventCardDebugInfo = loadable(
    () => import('./SearchEventCardDebugInfo'),
);

export interface SearchResultEventCardProps
    extends SearchEventCardStylesSwitcherProps {
    shouldShowDebugOutput?: boolean;
    position: number;
    isExpansionsLoading: boolean;

    //Internal Tracking Callbacks that will need an audit
    trackClick?: (
        position: number,
        eventId: string,
        action: string,
        organizerFollowerCount: number,
    ) => void;
    trackClickInternal?: (
        position: number,
        eventId: string,
        ticketsBy: string,
    ) => void;
}

/**
 * Temporary formatting layer to translate the incoming event props
 * to the FormattedEvent shape that the new event card is expecting.
 *
 * The plan is to use the transformation util exported by EventRenderer
 * to format events and standardize this, but in flight PR updating
 * state management should land first. In the interim doing this formatting
 * layer to align the new card with current structure.
 *
 *
 *
 * @param props
 * @returns
 */
export const SearchResultEventCard: React.FunctionComponent<
    SearchResultEventCardProps
> = ({
    event,
    position,
    shouldShowDebugOutput,
    trackClickInternal,
    trackClick,
    ...props
}) => {
    const {
        id,
        ticketsBy,
        organizerFollowerCount = 0,
        isPromoted,
        name,
    } = event;
    const shouldShowExpandDedupedEventCTA =
        event.dedup &&
        typeof event.dedup.count === 'number' &&
        event.dedup.count > 1;

    const renderDebugInfo =
        event.debugInfo?.esDebugInfo && shouldShowDebugOutput;

    const onClick = React.useCallback(() => {
        //position 1 indexed, but tracking expects 0 indexed values
        const rank = position - 1;
        trackClick?.(
            rank,
            id,
            ticketsBy === 'Eventbrite'
                ? GA_VIEW_EVENT_ACTION
                : GA_EXTERNAL_VIEW_EVENT_ACTION,
            organizerFollowerCount,
        );
        trackClickInternal?.(rank, id, ticketsBy || 'Eventbrite');
    }, [
        trackClickInternal,
        trackClick,
        id,
        ticketsBy,
        organizerFollowerCount,
        position,
    ]);

    return (
        <>
            <SearchEventCardStyleSwitcher
                event={event}
                onClick={onClick}
                moreActions={
                    isPromoted && <PromotedActionsMenuEventCard event={event} />
                }
                {...props}
            />
            {shouldShowExpandDedupedEventCTA ? (
                <ExpandDedupedEventCTA
                    dedup={event.dedup}
                    name={name}
                    eventId={id}
                />
            ) : null}
            {renderDebugInfo ? (
                <SearchEventCardDebugInfo
                    debugInfo={event.debugInfo}
                    position={position}
                    numFollowers={organizerFollowerCount}
                />
            ) : null}
        </>
    );
};

export default SearchResultEventCard;
